/**
 * Readable URLs for CMS records keyed by UUID.
 *
 * Blogs and services are identified by a UUID with no slug field, and adding one
 * means a CMS schema change plus a backfill. Until that happens, a URL of
 * /blogs/f0304eed-660c-4995-ba34-ed021305bbea is shareable but tells a reader
 * and a crawler nothing.
 *
 * So the path carries a slug derived from the title with a short id suffix:
 *
 *   /blogs/agentic-ai-in-workflows-f0304eed
 *
 * The suffix is what resolves the record; the words in front are for humans and
 * search. A retitled post changes its URL but the old one still resolves, since
 * only the suffix is read — which is the behaviour you want during an edit, and
 * why the suffix goes last rather than first.
 */

/** Length of the id fragment kept in the URL. */
const ID_FRAGMENT = 8;

export function slugify(text: string | undefined | null): string {
    return String(text || "")
        .toLowerCase()
        .replace(/<[^>]*>/g, " ")      // titles in this CMS contain markup
        .replace(/&[a-z]+;/g, " ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60)
        .replace(/-+$/g, "");
}

/** Builds the URL segment for a record. */
export function toSlug(title: string | undefined | null, id: string | undefined | null): string {
    const words = slugify(title ?? "");
    const safeId = String(id || "").replace(/-/g, "");
    const fragment = safeId.slice(0, ID_FRAGMENT);
    return words ? `${words}-${fragment}` : fragment;
}

/**
 * Recovers the id fragment from a slug. Returns the whole string when there is
 * no recognisable suffix, so a bare id in the URL still works — old links and
 * hand-typed ones both resolve.
 */
export function fragmentFromSlug(slug: string | undefined | null): string {
    if (!slug || typeof slug !== "string") return "";
    const parts = slug.split("-");
    const last = parts[parts.length - 1] ?? "";
    return /^[a-f0-9]{6,}$/i.test(last) ? last : slug.replace(/-/g, "");
}

/** Finds a record whose id starts with the fragment carried by the slug. */
export function findBySlug<T extends { id?: string; blogId?: string }>(
    items: T[],
    slug: string | undefined | null
): T | undefined {
    if (!slug || typeof slug !== "string") return undefined;
    const fragment = fragmentFromSlug(slug).toLowerCase();
    if (!fragment) return undefined;
    return (items || []).find((item) => {
        const id = String(item.id ?? item.blogId ?? "").replace(/-/g, "").toLowerCase();
        return id.startsWith(fragment) || id === fragment;
    });
}
